import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  CircularProgress,
  Slider
} from '@mui/material';
import { formatPercentage, formatDuration, formatCurrency, formatPercentageAxis } from '../utils/formatting';
import { LoanOffer, Collection } from '../types';
import { useCollections } from '../hooks/useCollections';

interface InputControlsProps {
  onUserOfferChange: (userOffer: Partial<LoanOffer>) => void;
  userOffer: Partial<LoanOffer>;
  selectedCurrency: 'WETH' | 'USDC';
  showContours?: boolean;
  onShowContoursChange?: (show: boolean) => void;
  showActiveLoans?: boolean;
  onShowActiveLoansChange?: (show: boolean) => void;
  activeLoanDaysWindow?: number;
  setActiveLoanDaysWindow?: (days: number) => void;
  maxActiveLoanDays?: number;
  showSlider?: boolean;
}

const InputControls: React.FC<InputControlsProps> = ({ 
  onUserOfferChange,
  userOffer,
  selectedCurrency,
  showContours = true,
  onShowContoursChange,
  showActiveLoans = false,
  onShowActiveLoansChange,
  activeLoanDaysWindow = 1,
  setActiveLoanDaysWindow,
  maxActiveLoanDays = 1,
  showSlider = false
}) => {
  const { collections, loading, error } = useCollections();
  
  // Add local state for input display
  const [loanAmountInput, setLoanAmountInput] = React.useState<string>('');
  const [interestRateInput, setInterestRateInput] = React.useState<string>('');
  
  // Set default collection to Wrapped Crypto Punks when component loads
  React.useEffect(() => {
    if (!loading && collections.length > 0 && !userOffer.collectionAddress) {
      const wrappedPunks = collections.find(c => 
        c.name.toLowerCase().includes('wrapped') && 
        c.name.toLowerCase().includes('cryptopunks')
      );
      if (wrappedPunks) {
        onUserOfferChange({ 
          collection: wrappedPunks.name,
          collectionAddress: wrappedPunks.contract_address 
        });
      }
    }
  }, [loading, collections, userOffer.collectionAddress, onUserOfferChange]);

  // Sync input when userOffer.loanAmount changes externally
  React.useEffect(() => {
    if (userOffer.loanAmount !== undefined && !isNaN(userOffer.loanAmount)) {
      setLoanAmountInput(userOffer.loanAmount.toString());
    } else {
      setLoanAmountInput('');
    }
  }, [userOffer.loanAmount]);

  // Sync interest rate input
  React.useEffect(() => {
    if (userOffer.interestRate !== undefined && !isNaN(userOffer.interestRate)) {
      setInterestRateInput(userOffer.interestRate.toString());
    } else {
      setInterestRateInput('');
    }
  }, [userOffer.interestRate]);

  // Filter out unwanted collections by name or slug
  const filteredCollections = collections.filter(
    (c) => {
      const name = c.name.toLowerCase();
      const slug = c.slug?.toLowerCase?.() || '';
      // Exclude 'cryptopunks', 'cryptopunks 721' (but NOT wrapped cryptopunks), and 'art blocks curated'
      if (
        name === 'cryptopunks' ||
        slug === 'cryptopunks' ||
        name === 'cryptopunks 721' ||
        slug === 'cryptopunks-721' ||
        name === 'art blocks curated' ||
        slug === 'art-blocks-curated'
      ) {
        // Allow wrapped cryptopunks (e.g., "CryptoPunks V1 (wrapped)")
        if (name.includes('wrapped')) return true;
        return false;
      }
      return true;
    }
  );

  // Handlers for controlled fields
  const handleCollectionChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const selectedCollection = collections.find(c => c.contract_address === value);
    if (selectedCollection) {
      onUserOfferChange({ 
        collection: selectedCollection.name,
        collectionAddress: selectedCollection.contract_address 
      });
    }
  };

  const handleLoanAmountChange = (value: string) => {
    setLoanAmountInput(value);
  };

  const handleLoanAmountBlur = () => {
    const numValue = Number(loanAmountInput.replace(/[^0-9.]/g, ''));
    if (!isNaN(numValue) && loanAmountInput.trim() !== '') {
      onUserOfferChange({ loanAmount: numValue });
    } else {
      onUserOfferChange({ loanAmount: undefined });
    }
    // Optionally format after blur
    setLoanAmountInput(
      !isNaN(numValue) && loanAmountInput.trim() !== '' ? numValue.toString() : ''
    );
  };

  const handleLoanAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleInterestRateChange = (value: string) => {
    setInterestRateInput(value);
  };

  const handleInterestRateBlur = () => {
    const numValue = Number(interestRateInput.replace(/[^0-9.]/g, ''));
    if (!isNaN(numValue) && interestRateInput.trim() !== '') {
      onUserOfferChange({ interestRate: numValue });
    } else {
      onUserOfferChange({ interestRate: undefined });
    }
    setInterestRateInput(
      !isNaN(numValue) && interestRateInput.trim() !== '' ? numValue.toString() : ''
    );
  };

  const handleInterestRateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleDurationDropdownChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === 'all') {
      onUserOfferChange({ duration: undefined });
    } else {
      // Convert seconds to days for storage
      const durationInDays = Number(value) / 86400;
      onUserOfferChange({ duration: durationInDays });
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        boxShadow: '0px 0px 2px rgba(0,0,0,0.24), 0px 12px 24px -4px rgba(0,0,0,0.24)',
        borderRadius: '16px',
      }}
    >
      <Typography variant="h6" gutterBottom>
        Your Offer
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Collection Selection */}
        <FormControl fullWidth>
          <InputLabel id="collection-select-label">Collection</InputLabel>
          <Select
            labelId="collection-select-label"
            id="collection-select"
            value={userOffer.collectionAddress || ''}
            label="Collection"
            onChange={handleCollectionChange}
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading collections...
              </MenuItem>
            ) : error ? (
              <MenuItem disabled>Error loading collections</MenuItem>
            ) : (
              filteredCollections.map((collection) => (
                <MenuItem 
                  key={`${collection.contract_address}-${collection.name}`} 
                  value={collection.contract_address}
                >
                  {collection.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Loan Amount Input */}
        <TextField
          fullWidth
          label="Loan Amount"
          type="text"
          value={loanAmountInput}
          onChange={(e) => handleLoanAmountChange(e.target.value)}
          onBlur={handleLoanAmountBlur}
          onKeyDown={handleLoanAmountKeyDown}
          InputProps={{
            endAdornment: <Typography variant="body2">{selectedCurrency}</Typography>,
          }}
        />

        {/* Duration Dropdown */}
        <FormControl fullWidth>
          <InputLabel id="duration-select-label">Duration</InputLabel>
          <Select
            labelId="duration-select-label"
            id="duration-select"
            value={
              userOffer.duration === undefined
                ? 'all'
                : (Math.round(userOffer.duration * 86400)).toString()
            }
            label="Duration"
            onChange={handleDurationDropdownChange}
          >
            <MenuItem value="all">All Durations</MenuItem>
            <MenuItem value="604800">7 Days</MenuItem>
            <MenuItem value="1209600">14 Days</MenuItem>
            <MenuItem value="2592000">30 Days</MenuItem>
            <MenuItem value="5184000">60 Days</MenuItem>
            <MenuItem value="7776000">90 Days</MenuItem>
          </Select>
        </FormControl>

        {/* Interest Rate Input */}
        <TextField
          fullWidth
          label="Interest Rate"
          type="text"
          value={interestRateInput}
          onChange={(e) => handleInterestRateChange(e.target.value)}
          onBlur={handleInterestRateBlur}
          onKeyDown={handleInterestRateKeyDown}
          InputProps={{
            endAdornment: <Typography variant="body2">%</Typography>,
          }}
        />

        {/* Show Contours Toggle */}
        {onShowContoursChange && (
          <FormControlLabel
            control={
              <Switch
                checked={showContours}
                onChange={(e) => onShowContoursChange(e.target.checked)}
              />
            }
            label="Loan depth"
          />
        )}

        {/* Show Active Loans Toggle */}
        {onShowActiveLoansChange && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveLoans}
                  onChange={(e) => onShowActiveLoansChange(e.target.checked)}
                />
              }
              label="Active loans"
            />
            {/* Slider for active loan days window */}
            {showSlider && (
              <Box sx={{ mt: 1, px: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Active loans from last {activeLoanDaysWindow} day{activeLoanDaysWindow > 1 ? 's' : ''}
                </Typography>
                <Slider
                  min={1}
                  max={maxActiveLoanDays}
                  value={activeLoanDaysWindow}
                  onChange={(_, val) => setActiveLoanDaysWindow && setActiveLoanDaysWindow(Number(val))}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 1, label: '1 day' },
                    { value: maxActiveLoanDays, label: `${maxActiveLoanDays} days` }
                  ]}
                  sx={{ width: '100%' }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default InputControls; 